import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  CheckCircle, XCircle, Clock, Trophy, AlertCircle, 
  ChevronRight, RotateCcw, Loader2, Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function QuizComponent({ contentId, token, onQuizPassed }) {
  const [quizData, setQuizData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attemptData, setAttemptData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadQuiz();
  }, [contentId]);

  useEffect(() => {
    // Timer countdown
    if (timeLeft === null || timeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit(); // Auto-submit when time runs out
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const loadQuiz = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/quizzes/content/${contentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuizData(response.data);
    } catch (err) {
      setError('Error cargando el examen');
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = async () => {
    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API_URL}/api/quizzes/${quizData.quiz.id}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAttemptData(response.data);
      setCurrentQuestion(0);
      setAnswers({});
      setResults(null);
      
      if (response.data.time_limit_minutes) {
        setTimeLeft(response.data.time_limit_minutes * 60);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error iniciando el examen');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmit = async () => {
    if (!attemptData) return;
    
    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API_URL}/api/quizzes/${quizData.quiz.id}/submit/${attemptData.attempt_id}`,
        { answers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResults(response.data);
      setAttemptData(null);
      setTimeLeft(null);
      
      if (response.data.passed && onQuizPassed) {
        onQuizPassed();
      }
      
      // Reload quiz data to update attempts
      loadQuiz();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error enviando el examen');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!quizData?.has_quiz) {
    return null;
  }

  const { quiz, questions, user_status } = quizData;

  // Show results
  if (results) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className={`text-center mb-6 ${results.passed ? 'text-green-600' : 'text-red-600'}`}>
          {results.passed ? (
            <>
              <Trophy className="w-16 h-16 mx-auto mb-3" />
              <h2 className="text-2xl font-bold">¡Felicidades!</h2>
              <p className="text-gray-600">Has aprobado el examen</p>
            </>
          ) : (
            <>
              <XCircle className="w-16 h-16 mx-auto mb-3" />
              <h2 className="text-2xl font-bold">No aprobaste</h2>
              <p className="text-gray-600">Necesitas {quiz.passing_score}% para aprobar</p>
            </>
          )}
        </div>

        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-indigo-600">{Math.round(results.score)}%</p>
              <p className="text-sm text-gray-500">Tu puntaje</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{results.points_earned}/{results.total_points}</p>
              <p className="text-sm text-gray-500">Puntos</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{formatTime(results.time_taken_seconds)}</p>
              <p className="text-sm text-gray-500">Tiempo</p>
            </div>
          </div>
        </div>

        {/* Show answers review */}
        <div className="space-y-4 mb-6">
          <h3 className="font-semibold text-gray-900">Revisión de respuestas</h3>
          {results.results.map((r, i) => {
            const q = questions.find(q => q.id === r.question_id);
            return (
              <div 
                key={r.question_id}
                className={`p-4 rounded-lg border ${r.is_correct ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}
              >
                <div className="flex items-start gap-3">
                  {r.is_correct ? (
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{q?.question_text}</p>
                    <p className="text-sm mt-1">
                      Tu respuesta: <span className={r.is_correct ? 'text-green-700' : 'text-red-700'}>{r.your_answer}</span>
                    </p>
                    {!r.is_correct && r.correct_answer && (
                      <p className="text-sm text-green-700">Respuesta correcta: {r.correct_answer}</p>
                    )}
                    {r.explanation && (
                      <p className="text-sm text-gray-600 mt-2 italic">{r.explanation}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!results.passed && results.attempts_remaining > 0 && (
          <button
            onClick={() => setResults(null)}
            className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-5 h-5" />
            Intentar de nuevo ({results.attempts_remaining} intentos restantes)
          </button>
        )}

        {results.certificate_unlocked && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 text-amber-700">
              <Award className="w-5 h-5" />
              <span className="font-medium">¡Certificado desbloqueado!</span>
            </div>
            <p className="text-sm text-amber-600 mt-1">
              Ahora puedes obtener tu certificado de finalización.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Show quiz in progress
  if (attemptData) {
    const question = attemptData.questions[currentQuestion];
    const isLastQuestion = currentQuestion === attemptData.questions.length - 1;
    const answeredCount = Object.keys(answers).length;

    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        {/* Header with timer */}
        <div className="flex items-center justify-between mb-6">
          <span className="text-sm text-gray-500">
            Pregunta {currentQuestion + 1} de {attemptData.questions.length}
          </span>
          {timeLeft !== null && (
            <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${timeLeft < 60 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
              <Clock className="w-4 h-4" />
              <span className="font-mono font-medium">{formatTime(timeLeft)}</span>
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
          <div
            className="bg-indigo-600 h-2 rounded-full transition-all"
            style={{ width: `${((currentQuestion + 1) / attemptData.questions.length) * 100}%` }}
          />
        </div>

        {/* Question */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{question.question_text}</h3>
          
          {question.question_type === 'multiple_choice' && (
            <div className="space-y-3">
              {question.options.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => handleAnswer(question.id, opt.id)}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                    answers[question.id] === opt.id
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full mr-3 text-sm font-medium ${
                    answers[question.id] === opt.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {opt.id.toUpperCase()}
                  </span>
                  {opt.text}
                </button>
              ))}
            </div>
          )}

          {question.question_type === 'true_false' && (
            <div className="flex gap-4">
              {['true', 'false'].map(val => (
                <button
                  key={val}
                  onClick={() => handleAnswer(question.id, val)}
                  className={`flex-1 p-4 rounded-lg border-2 font-medium ${
                    answers[question.id] === val
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {val === 'true' ? 'Verdadero' : 'Falso'}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
            disabled={currentQuestion === 0}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50"
          >
            Anterior
          </button>

          <span className="text-sm text-gray-500">
            {answeredCount} de {attemptData.questions.length} respondidas
          </span>

          {isLastQuestion ? (
            <button
              onClick={handleSubmit}
              disabled={submitting || answeredCount < attemptData.questions.length}
              className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 flex items-center gap-2"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>Enviar examen</>
              )}
            </button>
          ) : (
            <button
              onClick={() => setCurrentQuestion(prev => prev + 1)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-1"
            >
              Siguiente <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Show quiz intro
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-3 bg-indigo-100 rounded-lg">
          <Award className="w-6 h-6 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">{quiz.title}</h2>
          {quiz.is_required_for_certificate && (
            <p className="text-sm text-amber-600">Requerido para obtener certificado</p>
          )}
        </div>
      </div>

      {quiz.description && (
        <p className="text-gray-600 mb-6">{quiz.description}</p>
      )}

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-indigo-600">{quiz.total_questions}</p>
          <p className="text-sm text-gray-500">Preguntas</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-indigo-600">{quiz.passing_score}%</p>
          <p className="text-sm text-gray-500">Para aprobar</p>
        </div>
        {quiz.time_limit_minutes && (
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-indigo-600">{quiz.time_limit_minutes}</p>
            <p className="text-sm text-gray-500">Minutos</p>
          </div>
        )}
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-indigo-600">{quiz.max_attempts}</p>
          <p className="text-sm text-gray-500">Intentos máx.</p>
        </div>
      </div>

      {/* User status */}
      {user_status.has_passed ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">¡Ya aprobaste este examen!</span>
          </div>
          <p className="text-sm text-green-600 mt-1">Tu mejor puntaje: {Math.round(user_status.best_score)}%</p>
        </div>
      ) : user_status.attempts_count > 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Intentos anteriores: {user_status.attempts_count}</span>
          </div>
          <p className="text-sm text-amber-600 mt-1">
            Mejor puntaje: {Math.round(user_status.best_score)}% • 
            Intentos restantes: {user_status.attempts_remaining}
          </p>
        </div>
      ) : null}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      <button
        onClick={startQuiz}
        disabled={!user_status.can_attempt || submitting}
        className="w-full py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
        data-testid="start-quiz-btn"
      >
        {submitting ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : user_status.has_passed ? (
          'Examen aprobado'
        ) : !user_status.can_attempt ? (
          'Sin intentos disponibles'
        ) : (
          <>Comenzar examen</>
        )}
      </button>
    </div>
  );
}
